import { type Component, Show } from "solid-js";
import {
  ASTNodeRenderer,
  type MarkdownRendererOptions,
  SolidStreamingMarkdownContextProvider,
} from "./components/ASTRenderers";
import type { DocASTNode } from "./toastmark/ast";

export { createSolidStreamingMarkdown, markdownToHtml, parseMarkdown, stripMarkdownToPlainText } from "./core";

/**
 * Renders a markdown document.
 *
 * Accepts a reactive markdown document node generated by `createSolidStreamingMarkdown` and
 * renders it into HTML.
 *
 * @example
 * ```ts
 * const parser = createSolidStreamingMarkdown("# Hello world");
 * <SolidStreamingMarkdown doc={parser.doc} />
 * ```
 */
export const SolidStreamingMarkdown: Component<{
  doc: DocASTNode | undefined;
  options: MarkdownRendererOptions;
  /**
   * Renders the document once, with no reactive node per AST node. Set it for content that has
   * finished arriving, and pass a `parseMarkdown` document rather than a streaming store — a store
   * read under this path still allocates its per-property signals, which is the cost being avoided.
   *
   * Turning it on mid-stream swaps the subtree over, so a caller that freezes a live document has
   * to hand over the finished document in the same update.
   */
  static?: boolean;
}> = (props) => {
  const tree = (doc: DocASTNode, isStatic: boolean) => (
    <SolidStreamingMarkdownContextProvider options={props.options} static={isStatic}>
      <ASTNodeRenderer node={doc} />
    </SolidStreamingMarkdownContextProvider>
  );

  // Keyed on the document, because static output is built once and a replaced document has to
  // rebuild the subtree; the streaming path keeps one store root for its lifetime and never
  // re-keys. The inner condition memoizes on truthiness, so freezing swaps the subtree exactly once.
  return (
    <Show when={props.doc} keyed>
      {(doc) => (
        <Show when={props.static} fallback={tree(doc, false)}>
          {tree(doc, true)}
        </Show>
      )}
    </Show>
  );
};
